<template>
  <div :class="`filter-box`">
    <div class="filter-box__content" id="filter-box-content" @keydown.esc="closeAllExpanders">
      <feature-group
        v-if="!rowFilters.featureGroup && filters"
        :expanderVisible="isExpanderVisible('featureGroup')"
        @toggle="toggleExpander('featureGroup')"
        :currentGroup="filters.featureGroup"
        :mode="filters.mode"
      ></feature-group>
      <location v-if="isFilterVisible('location')" :isMobile="true"></location>
      <filter-range
        v-if="isFilterVisible('priceMin')"
        filterName="price"
        label="Myyntihinta"
        :limitMin="config.FILTER_LIMITS.PRICE_MIN"
        :limitMax="config.FILTER_LIMITS.PRICE_MAX"
        :currentMin="filters?.priceMin"
        :currentMax="filters?.priceMax"
        unit="€"
        :step="1000"
        ariaLabel="Valitse hinnan vaihteluväli euroina"
      ></filter-range>
      <filter-range
        v-if="isFilterVisible('rentMin')"
        filterName="rent"
        label="Vuokra"
        :limitMin="config.FILTER_LIMITS.RENT_MIN"
        :limitMax="config.FILTER_LIMITS.RENT_MAX"
        :currentMin="filters?.rentMin"
        :currentMax="filters?.rentMax"
        unit="€/kk"
        :step="10"
        ariaLabel="Valitse vuokran vaihteluväli euroina kuukaudessa"
      ></filter-range>
      <filter-range
        key="livingArea"
        v-if="isFilterVisible('livingAreaMin')"
        filterName="livingArea"
        :label="areaRangeLabel"
        :limitMin="config.FILTER_LIMITS.AREA_MIN"
        :limitMax="config.FILTER_LIMITS.AREA_MAX"
        :currentMin="filters?.livingAreaMin"
        :currentMax="filters?.livingAreaMax"
        unit="m²"
        ariaLabel="Valitse pinta-alan vaihteluväli yksikkönä {{ unit }}"
      ></filter-range>
      <filter-range
        key="siteArea"
        v-if="isFilterVisible('siteAreaMin')"
        filterName="siteArea"
        :label="areaRangeLabel"
        :limitMin="config.FILTER_LIMITS.SITE_AREA_MIN"
        :limitMax="config.FILTER_LIMITS.SITE_AREA_MAX"
        :currentMin="filters?.siteAreaMin"
        :currentMax="filters?.siteAreaMax"
        unit="m²"
        :step="100"
        ariaLabel="Valitse tontin pinta-alan vaihteluväli yksikkönä m²"
      ></filter-range>
      <filter-range
        key="totalArea"
        v-if="isFilterVisible('totalAreaMin')"
        filterName="totalArea"
        :label="areaRangeLabel"
        :limitMin="config.FILTER_LIMITS.TOTAL_AREA_MIN"
        :limitMax="config.FILTER_LIMITS.TOTAL_AREA_MAX"
        :currentMin="filters?.totalAreaMin"
        :currentMax="filters?.totalAreaMax"
        unit="m²"
        :step="25"
        ariaLabel="Valitse kokonaispinta-alan vaihteluväli yksikkönä m²"
      ></filter-range>
      <number-of-rooms v-if="isFilterVisible('numberOfRooms')" :numberOfRooms="filters?.numberOfRooms">
      </number-of-rooms>
      <filter-range
        v-if="isFilterVisible('yearMin')"
        filterName="year"
        label="Käyttöönottovuosi"
        :limitMin="config.FILTER_LIMITS.YEAR_MIN"
        :limitMax="config.FILTER_LIMITS.YEAR_MAX"
        :currentMin="filters?.yearMin"
        :currentMax="filters?.yearMax"
        ariaLabel="Valitse käyttöönottovuoden vaihteluväli vuosina"
      ></filter-range>
      <listing-types
        v-if="isFilterVisible('listingTypes')"
        :feature-group="filters?.featureGroup"
        :selected-types="filters?.type"
      ></listing-types>
      <extra-features
        v-if="isFilterVisible('extraFeatures')"
        :feature-group="filters?.featureGroup"
        :selected-features="filters?.feature"
      ></extra-features>
      <condition v-if="isFilterVisible('condition')" :selectedConditions="filters?.condition"></condition>
      <property-id v-if="isFilterVisible('propertyId')"></property-id>
      <showings v-if="isFilterVisible('showings')" :selectedShowingIn="filters?.showingIn"></showings>
      <site-ownership
        v-if="isFilterVisible('siteOwnership')"
        :selectedModeOfOwnership="filters?.siteOwnership"
      ></site-ownership>
      <energy-classification v-if="isFilterVisible('energy')" :energyClasses="filters?.energy"></energy-classification>
    </div>

    <footer :class="actionBarClass()">
      <form-button
        :text="`Näytä tulokset (${locationTotalCount})`"
        theme="primary"
        @click.native="selectListingTab()"
      ></form-button>
      <button class="filter-action clear" @click="clearFilters()">
        <i class="filter-icon icon-trashcan"></i>
      </button>
    </footer>
  </div>
</template>

<script lang="ts">
import config from '@/config';
import FeatureGroup from './feature-group.vue';
import ListingTypes from './listing-types.vue';
import Location from './location.vue';
import NumberOfRooms from './number-of-rooms.vue';
import Condition from './condition.vue';
import PropertyId from './property-id.vue';
import ExtraFeatures from './extra-features.vue';
import Showings from './showings.vue';
import SiteOwnership from './site-ownership.vue';
import EnergyClassification from './energy-classification.vue';
import searchUtils from '@/app/utils/search-utils';
import { defineAsyncComponent, defineComponent } from 'vue';
import type { PropType } from 'vue';
import { getAreaRangeLabel } from '@/utils/texts';
import { getFeatureGroupById } from '@/config/featureGroups';
import type { FeatureGroupFiltersType, FilterViewType } from '@/config/featureGroups';
import useSafeRoute from '@/app/composables/useSafeRoute';
import type { FiltersType } from '@/schemas/filtersSchema';
import FilterRange from './filter-range.vue';
import type { RowFilterType } from './filterTypes';

export default defineComponent({
  name: 'filter-box',
  setup() {
    const route = useSafeRoute();
    return {
      route,
    };
  },
  props: {
    rowFilters: {
      type: Object as PropType<RowFilterType>,
      required: true,
    },
    locationTotalCount: {
      type: Number,
      required: false,
      default: 0,
    },
    filters: {
      type: Object as PropType<FiltersType>,
      required: false,
    },
  },
  data: () => ({
    filterBoxClass: '',
    config,
    actionBarVisible: true,
    listener: null,
    openExpanders: new Set(),
  }),
  computed: {
    featureGroupFilters() {
      return getFeatureGroupById(this.filters?.featureGroup)?.filters[
        this.filters?.mode as keyof FeatureGroupFiltersType
      ];
    },
    areaRangeLabel() {
      return getAreaRangeLabel(this.filters?.featureGroup);
    },
  },
  methods: {
    closeAllExpanders() {
      this.openExpanders.clear();
    },
    toggleExpander(expander: string) {
      if (this.openExpanders.has(expander)) {
        this.openExpanders.delete(expander);
      } else {
        this.openExpanders.add(expander);
      }
    },
    isExpanderVisible(expander: string) {
      return this.openExpanders.has(expander);
    },
    actionBarClass() {
      return this.actionBarVisible ? 'filter-box__actions' : 'filter-box__actions is-hidden';
    },
    isFilterVisible(filter: FilterViewType) {
      return !this.rowFilters[filter] && this.featureGroupFilters?.includes(filter);
    },
    clearFilters() {
      searchUtils.removeAllFilters(this.$router, this.route);
    },
    selectListingTab() {
      window.scrollTo(0, 0);
      this.$bus.$emit('search-selectedMobileTab', config.MOBILE_TABS.LIST);
    },
  },
  mounted() {
    this.$bus.$on(
      'inputFieldFocus',
      () => {
        this.actionBarVisible = false;
      },
      this
    );

    this.$bus.$on(
      'inputFieldBlur',
      () => {
        this.actionBarVisible = true;
      },
      this
    );
  },
  beforeUnmount() {
    this.$bus.$off('inputFieldFocus', this);
    this.$bus.$off('inputFieldBlur', this);
  },
  components: {
    PropertyId,
    FeatureGroup,
    Location,
    NumberOfRooms,
    Condition,
    FormButton: defineAsyncComponent(() => import('@/app/components/form-button/form-button.vue')),
    ListingTypes,
    ExtraFeatures,
    Showings,
    SiteOwnership,
    EnergyClassification,
    FilterRange,
  },
});
</script>
